import {
  Button,
  Dialog,
  DialogProps,
  Divider,
  TableProps,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Widget } from '~/components/MyWorkspace/Widget';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { useVisible } from '~/hooks/useVisible';

import { AllTableDialogContentComponentFactory } from './components/AllTableDialogContent';
import { ContentComponentFactory } from './components/Content';

export interface TableRowComponentProps<TR> {
  tableRow: TR;
  isTabDisabled?: boolean;
}

export interface Props<TR> {
  title: string;
  data?: TR[];
  loading: boolean;
  failedToLoad: boolean;
  noDataMessage: string;
  onAddTableRowClick?: () => void;
  addTableRowUrl?: string;
  tableProps?: TableProps;
  isTabDisabled?: boolean;
  customActions?: React.ReactElement;
}

type TableWidgetComponentFactoryPropsType<TR> = {
  TableRowComponent: React.ComponentType<TableRowComponentProps<TR>>;
  getIdentifier: (item: TR) => string | number;
  TableHeaderComponent?: React.ComponentType;
  TitleComponent?: React.ComponentType<{ title: string; tableRows?: TR[] }>;
  dataCy?: string;
  allTableDialogProps?: Partial<DialogProps>;
};

export const TableWidgetComponentFactory = <TR extends {}>(
  props: TableWidgetComponentFactoryPropsType<TR>,
) => {
  const {
    TableRowComponent,
    getIdentifier,
    TableHeaderComponent,
    TitleComponent,
    dataCy,
    allTableDialogProps,
  } = props;

  const Content = ContentComponentFactory(
    TableRowComponent,
    TableHeaderComponent,
  );
  const AllTableItemsDialogContent = AllTableDialogContentComponentFactory(
    TableRowComponent,
    getIdentifier,
    TableHeaderComponent,
  );

  const Component: React.FC<Props<TR>> = ({
    title,
    data,
    loading,
    failedToLoad,
    noDataMessage,
    onAddTableRowClick,
    addTableRowUrl,
    tableProps,
    isTabDisabled,
    customActions,
  }) => {
    const { t } = useTranslation();

    const {
      isVisible: isAllTableDialogOpen,
      show: openAllTableDialog,
      hide: closeAllTableDialog,
    } = useVisible();

    const [noOfFittingTableItems, setNoOfFittingTableItems] =
      useState<number>(0);

    const { isAllTableDialogDisabled } = useMemo(() => {
      const itemCount = (!!data && data.length) || 0;

      return {
        hiddenItemCount:
          itemCount > noOfFittingTableItems
            ? itemCount - noOfFittingTableItems
            : 0,
        isAllTableDialogDisabled: !data || !(itemCount > 0),
      };
    }, [data, noOfFittingTableItems]);

    return (
      <>
        <Widget
          title={
            (TitleComponent && (
              <TitleComponent title={title} tableRows={data} />
            )) ||
            title
          }
          content={
            <Content
              tableRowsLoading={loading}
              tableRows={data?.slice(0, noOfFittingTableItems)}
              failedToLoadTableItems={failedToLoad}
              noDataMessage={noDataMessage}
              getIdentifier={getIdentifier}
              onResize={setNoOfFittingTableItems}
              tableProps={tableProps}
              isTabDisabled={isTabDisabled}
            />
          }
          actions={
            <>
              <Button
                size="small"
                color="primary"
                disabled={isAllTableDialogDisabled}
                onClick={openAllTableDialog}
                data-cy="show-all"
              >
                {t('showAll')}
              </Button>
              {(onAddTableRowClick || addTableRowUrl) && (
                <Button
                  size="small"
                  color="primary"
                  onClick={onAddTableRowClick}
                  href={addTableRowUrl}
                >
                  {t('add')}
                </Button>
              )}
              {customActions}
            </>
          }
          dataCy={dataCy}
        />

        <Dialog
          {...allTableDialogProps}
          open={isAllTableDialogOpen}
          onClose={closeAllTableDialog}
        >
          <SolaForceDialogTitle onClose={closeAllTableDialog}>
            {title}
          </SolaForceDialogTitle>
          <Divider />
          <AllTableItemsDialogContent
            tableRows={data}
            isTabDisabled={isTabDisabled}
          />
        </Dialog>
      </>
    );
  };

  return Component;
};
